import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/auth";
import { PageHeader, Card, Btn, Icon } from "@/components/kit";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { HotBadge, LivePulse, SocialProof, LiveNudge, hotFor } from "@/components/Engagement";
import { toast } from "sonner";

const CATS = ["All", "Flexi Cap", "Large Cap", "Mid Cap", "Small Cap", "ELSS", "Hybrid", "Debt", "Index"];
const RISK_TONE = { "Low to Moderate": "#10B981", Moderate: "#10B981", "Moderately High": "#F59E0B", High: "#F97316", "Very High": "#EF4444" };
const Stars = ({ n }) => <span className="text-amber-400 text-xs">{"★".repeat(n)}<span className="text-gray-300">{"★".repeat(5 - n)}</span></span>;

export default function FundResearch() {
  const nav = useNavigate();
  const [funds, setFunds] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [sel, setSel] = useState(null);
  const [compare, setCompare] = useState([]);
  const [showCompare, setShowCompare] = useState(false);

  const load = useCallback(() => api.get("/funds", { params: { category: cat } }).then((r) => setFunds(r.data)).catch(() => {}), [cat]);
  useEffect(() => { load(); }, [load]);

  const view = useMemo(() => funds.filter((f) => f.name.toLowerCase().includes(q.toLowerCase()) || f.amc.toLowerCase().includes(q.toLowerCase())), [funds, q]);
  const toggleCompare = (f, e) => {
    e.stopPropagation();
    setCompare((c) => {
      if (c.find((x) => x.id === f.id)) return c.filter((x) => x.id !== f.id);
      if (c.length >= 4) { toast.error("Compare up to 4 funds"); return c; }
      return [...c, f];
    });
  };
  const askAI = (f) => nav("/app/ai", { state: { prompt: `Summarise this mutual fund for me in simple terms and who it suits: ${f.name} (${f.category}, ${f.amc}). 3Y return ${f.returns.y3}%, expense ${f.expense}%, risk ${f.risk}. Add a one-line risk disclaimer.` } });

  return (
    <div className="fade-up">
      <PageHeader title="Fund Research" subtitle="Explore and compare funds — performance, holdings, risk & cost." icon="LineChart" color="#3B82F6"
        action={compare.length >= 2 && <Btn onClick={() => setShowCompare(true)} data-testid="compare-open-btn"><Icon name="GitCompare" size={16} /> Compare ({compare.length})</Btn>} />

      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-4" data-testid="funds-live-strip">
        <LivePulse label="26 MFDs researching now" />
        <SocialProof icon="LineChart">Most compared: Flexi & Mid Cap</SocialProof>
        <SocialProof icon="Flame">Fund flows trending this week</SocialProof>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="flex items-center gap-2 flex-1 bg-white border border-gray-200 rounded-xl px-3 py-2.5 max-w-md">
          <Icon name="Search" size={16} className="text-gray-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search funds by name or AMC…" data-testid="fund-search" className="flex-1 text-sm outline-none bg-transparent" />
        </div>
      </div>
      <div className="flex gap-2 mb-5 overflow-x-auto no-scrollbar">
        {CATS.map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`fund-cat-${c.replace(/\s/g, "-").toLowerCase()}`}
            className={`px-3.5 py-1.5 rounded-full text-sm font-medium whitespace-nowrap border ${cat === c ? "grad-blue-violet text-white border-transparent" : "bg-white border-gray-200 text-gray-600"}`}>{c}</button>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="funds-grid">
        {view.map((f) => {
          const picked = compare.find((x) => x.id === f.id);
          return (
            <button key={f.id} onClick={() => setSel(f)} data-testid={`fund-card-${f.id}`} className={`text-left card-mfx p-5 hover-lift ${picked ? "ring-2 ring-blue-400" : ""}`}>
              <div className="flex items-start gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-sm leading-tight">{f.name}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{f.amc} · {f.category}</div>
                </div>
                <span onClick={(e) => toggleCompare(f, e)} data-testid={`fund-compare-${f.id}`} className={`ml-auto shrink-0 text-[10px] font-bold rounded-lg px-2 py-1 cursor-pointer ${picked ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-500"}`}>{picked ? "✓ Added" : "+ Compare"}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                {[["1Y", f.returns.y1], ["3Y", f.returns.y3], ["5Y", f.returns.y5]].map(([l, v]) => (
                  <div key={l}><div className="font-head font-bold text-base text-emerald-600">{v}%</div><div className="text-[10px] text-gray-400">{l} CAGR</div></div>
                ))}
              </div>
              <div className="flex items-center justify-between mt-3 text-xs text-gray-500">
                <Stars n={f.rating} />
                <span>Exp {f.expense}%</span>
                <span className="font-medium" style={{ color: RISK_TONE[f.risk] || "#F97316" }}>{f.risk}</span>
              </div>
              <div className="flex items-center justify-between mt-2">
                <HotBadge badge={hotFor(f.id).badge} />
                <SocialProof icon="Eye">{hotFor(f.id).weekViews} MFDs researched</SocialProof>
              </div>
            </button>
          );
        })}
      </div>

      {sel && <FundDetail fund={sel} onClose={() => setSel(null)} onAskAI={() => askAI(sel)} />}
      {showCompare && <CompareModal funds={compare} onClose={() => setShowCompare(false)} onClear={() => { setCompare([]); setShowCompare(false); }} />}

      <LiveNudge id="funds" title="Compare before you recommend" message="MFDs who compare 2–3 funds pick with more confidence. Add funds to compare." ctaLabel="Got it" />
    </div>
  );
}

function FundDetail({ fund: f, onClose, onAskAI }) {
  return (
    <div className="fixed inset-0 z-[90] bg-black/40 backdrop-blur-sm flex justify-end" onClick={onClose} data-testid="fund-detail-modal">
      <div className="w-full max-w-xl bg-[#FEF9F4] h-full overflow-y-auto no-scrollbar shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 glass px-6 py-4 flex items-center gap-3 border-b border-orange-100 z-10">
          <div><div className="font-head font-bold text-lg leading-tight">{f.name}</div><div className="text-xs text-gray-400">{f.amc} · {f.category} · <Stars n={f.rating} /></div></div>
          <button onClick={onClose} className="ml-auto text-gray-400 hover:text-gray-700" data-testid="fund-detail-close"><Icon name="X" size={20} /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-3 gap-3">
            {[["1Y", f.returns.y1], ["3Y", f.returns.y3], ["5Y", f.returns.y5]].map(([l, v]) => (
              <div key={l} className="card-mfx p-4 text-center"><div className="font-head font-bold text-xl text-emerald-600">{v}%</div><div className="text-[11px] text-gray-400">{l} CAGR</div></div>
            ))}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[["NAV", `₹${f.nav}`], ["AUM", f.aum], ["Expense", `${f.expense}%`], ["Risk", f.risk]].map(([l, v]) => (
              <div key={l} className="card-mfx p-3"><div className="text-[11px] text-gray-400">{l}</div><div className="font-semibold text-sm">{v}</div></div>
            ))}
          </div>
          <Btn onClick={onAskAI} data-testid="fund-ask-ai"><Icon name="Sparkles" size={15} /> Summarise this fund with AI</Btn>
          <Card>
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <div><span className="text-gray-400">Benchmark</span><div className="font-medium">{f.benchmark}</div></div>
              <div><span className="text-gray-400">Fund manager</span><div className="font-medium">{f.manager}</div></div>
            </div>
          </Card>
          <Card>
            <h4 className="font-head font-semibold mb-2">Sector allocation</h4>
            <ResponsiveContainer width="100%" height={160}>
              <PieChart><Pie data={f.sectors} dataKey="value" nameKey="name" innerRadius={40} outerRadius={70} paddingAngle={3}>
                {f.sectors.map((s, i) => <Cell key={s.name} fill={["#3B82F6", "#8B5CF6", "#F97316", "#06B6D4", "#A3E635"][i % 5]} />)}</Pie>
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #eee" }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-2 gap-1 mt-1">{f.sectors.map((s, i) => (
              <div key={s.name} className="flex items-center gap-1.5 text-xs"><span className="w-2 h-2 rounded-full" style={{ background: ["#3B82F6", "#8B5CF6", "#F97316", "#06B6D4", "#A3E635"][i % 5] }} />{s.name}<span className="ml-auto text-gray-400">{s.value}%</span></div>
            ))}</div>
          </Card>
          <Card>
            <h4 className="font-head font-semibold mb-3">Top holdings</h4>
            <div className="space-y-2">{f.holdings.map((h) => (
              <div key={h.name} className="flex items-center gap-3 text-sm"><span className="min-w-0 truncate">{h.name}</span><div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden"><div className="h-full bg-blue-500 rounded-full" style={{ width: `${Math.min(h.pct * 8, 100)}%` }} /></div><span className="text-gray-400 w-10 text-right">{h.pct}%</span></div>
            ))}</div>
          </Card>
          <div className="text-[11px] text-gray-400 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2 flex gap-2"><Icon name="ShieldAlert" size={13} className="text-amber-500 shrink-0 mt-0.5" /> Illustrative demo data. Past performance is not indicative of future returns. Not investment advice — mutual fund investments are subject to market risks.</div>
        </div>
      </div>
    </div>
  );
}

function CompareModal({ funds, onClose, onClear }) {
  const rows = [
    ["Category", (f) => f.category], ["1Y CAGR", (f) => `${f.returns.y1}%`], ["3Y CAGR", (f) => `${f.returns.y3}%`],
    ["5Y CAGR", (f) => `${f.returns.y5}%`], ["NAV", (f) => `₹${f.nav}`], ["AUM", (f) => f.aum],
    ["Expense", (f) => `${f.expense}%`], ["Risk", (f) => f.risk], ["Rating", (f) => "★".repeat(f.rating)], ["Benchmark", (f) => f.benchmark], ["Manager", (f) => f.manager],
  ];
  return (
    <div className="fixed inset-0 z-[95] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="compare-modal">
      <div className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white px-6 py-4 flex items-center gap-3 border-b border-gray-100">
          <h3 className="font-head font-bold text-lg">Compare funds</h3>
          <button onClick={onClear} className="ml-auto text-xs text-gray-400 hover:text-red-500">Clear</button>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="compare-close"><Icon name="X" size={20} /></button>
        </div>
        <div className="p-5 overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead><tr><th className="text-left p-2 text-gray-400 font-medium"> </th>{funds.map((f) => <th key={f.id} className="text-left p-2 font-head font-semibold min-w-[140px]">{f.name}</th>)}</tr></thead>
            <tbody>{rows.map(([label, fn]) => (
              <tr key={label} className="border-t border-gray-100"><td className="p-2 text-gray-400 whitespace-nowrap">{label}</td>{funds.map((f) => <td key={f.id} className="p-2 font-medium">{fn(f)}</td>)}</tr>
            ))}</tbody>
          </table>
          <div className="text-[11px] text-gray-400 mt-4">Illustrative demo data · past performance is not indicative of future returns · not investment advice.</div>
        </div>
      </div>
    </div>
  );
}
